import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

const products = [
  {
    title: 'SynteraCRM',
    category: 'Business Software',
    desc: 'All-in-one customer relationship management platform with automation, analytics, and integrations.',
    price: 'From £199/mo',
    tag: 'BESTSELLER',
    tagColor: '#FA6E4F',
    bg: '#F8CA9D',
  },
  {
    title: 'SynteraCloud',
    category: 'Infrastructure',
    desc: 'Scalable cloud hosting and server management with 99.9% uptime guarantee and global CDN.',
    price: 'From £49/mo',
    tag: 'POPULAR',
    tagColor: '#8EC9BB',
    bg: '#C5D7C0',
  },
  {
    title: 'SynteraAnalytics',
    category: 'Data & AI',
    desc: 'Real-time business intelligence dashboard with AI-powered insights and predictive analytics.',
    price: 'From £299/mo',
    tag: 'NEW',
    tagColor: '#FB8E7E',
    bg: '#FB8E7E',
  },
  {
    title: 'SynteraShield',
    category: 'Security',
    desc: 'Enterprise cybersecurity suite with threat detection, penetration testing, and compliance tools.',
    price: 'Custom',
    tag: 'PREMIUM',
    tagColor: '#F2CF59',
    bg: '#F2CF59',
  },
  {
    title: 'SynteraLaunch',
    category: 'Marketing Suite',
    desc: 'Complete digital marketing automation — SEO, social, email campaigns, and ad management.',
    price: '',
    tag: 'BUNDLE',
    tagColor: '#FA6E4F',
    bg: '#8EC9BB',
  },
  {
    title: 'SynteraAPI',
    category: 'Integration',
    desc: 'Robust REST and GraphQL APIs for seamless third-party integrations, data exchange, and microservices connectivity.',
    price: '',
    tag: 'DEV',
    tagColor: '#FB8E7E',
    bg: '#C5D7C0',
  },
];

export default function ProductsPage() {
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { clearTimeout(t); observer.disconnect(); };
  }, []);

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-screen flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '52vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          PROD
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-40 pb-24 max-w-3xl">
          <div className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-10 font-medium">
              &#8212;&nbsp;&nbsp;Our solutions&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(64px, 9vw, 130px)', lineHeight: 0.88 }}
            >
              OUR<br />
              <span style={{ color: '#FA6E4F' }}>PRODUCTS.</span>
            </h1>
          </div>
          <div className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              Powerful software products designed to streamline operations, boost growth, and protect your digital assets.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section ref={ref} className="px-10 lg:px-20 py-24">
        <div className={`mb-16 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}`}>
          <p className="text-[#FA6E4F]/60 text-xs tracking-[0.5em] uppercase mb-6">Built for business</p>
          <h2 className="boldonse text-white leading-none" style={{ fontSize: 'clamp(44px, 6vw, 90px)' }}>
            SOFTWARE<br />
            <span style={{ color: '#FA6E4F' }}>SUITE</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <div
              key={p.title}
              className="rounded-3xl overflow-hidden p-8 transition-all duration-500 hover:scale-[1.02]"
              style={{
                background: p.bg,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `all 0.8s ease ${i * 100}ms`,
              }}
            >
              <div className="flex justify-between items-start mb-6">
                <span className="boldonse text-xs px-3 py-1 rounded-full text-white whitespace-nowrap" style={{ background: p.tagColor }}>
                  {p.tag}
                </span>
              </div>
              <p className="text-white/60 text-xs tracking-widest uppercase mb-2">{p.category}</p>
              <h3 className="boldonse text-white text-2xl mb-4">{p.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">{p.desc}</p>
              <div className="mt-auto pt-4">
                <div
                  className="h-1 rounded-full w-16"
                  style={{ background: p.tagColor }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-10 lg:px-20 py-32 text-center">
        <p className="text-[#F2CF59] text-xs tracking-[0.5em] uppercase mb-10 font-medium">
          &#8212;&nbsp;&nbsp;Need something custom?&nbsp;&nbsp;&#8212;
        </p>
        <h2
          className="boldonse text-white leading-none mb-10"
          style={{ fontSize: 'clamp(52px, 7.5vw, 110px)', lineHeight: 0.9 }}
        >
          WE CAN<br />
          <span style={{ color: '#FA6E4F' }}>BUILD IT.</span>
        </h2>
        <a
          href="/contact"
          className="inline-block px-10 py-4 rounded-full boldonse text-sm tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          style={{ background: '#FA6E4F', color: '#fff' }}
        >
          GET IN TOUCH
        </a>
      </section>

      <Footer />
    </main>
  );
}